import React, { useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import volunteerService from '../services/volunteerService';
import { getOrganizationConfig } from '../../../utils/organizationConfig';
import { isValidUsPhone, isValidSignature, PHONE_ERROR, SIGNATURE_ERROR } from '../utils/volunteerValidation';
import './VolunteerRegistration.css';

const today = () => new Date().toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
const todayISO = () => new Date().toISOString().slice(0, 10);

const initForm = {
  firstName: '', lastName: '', email: '', phone: '',
  dob: '', parentName: '', comments: '',
  emergencyName: '', emergencyCell: '', signature: '',
};

export default function VolunteerSummerCamp() {
  const navigate = useNavigate();
  const abortRef = useRef(null);

  const [form, setForm] = useState(initForm);
  const [errors, setErrors] = useState({});
  const [waiver, setWaiver] = useState({ volunteerName: '' });
  const [waiverErrors, setWaiverErrors] = useState({});
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  const handleField = useCallback((field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    setErrors(prev => { const e = { ...prev }; delete e[field]; return e; });
  }, []);

  const handlePhoneField = useCallback((field, value) => {
    const digits = value.replace(/\D/g, '').slice(0, 10);
    let fmt = digits;
    if (digits.length > 6)      fmt = `(${digits.slice(0,3)}) ${digits.slice(3,6)}-${digits.slice(6)}`;
    else if (digits.length > 3) fmt = `(${digits.slice(0,3)}) ${digits.slice(3)}`;
    else if (digits.length > 0) fmt = `(${digits}`;
    setForm(prev => ({ ...prev, [field]: fmt }));
    setErrors(prev => { const e = { ...prev }; delete e[field]; return e; });
  }, []);

  const handleWaiverField = useCallback((field, value) => {
    setWaiver(prev => ({ ...prev, [field]: value }));
    setWaiverErrors(prev => { const e = { ...prev }; delete e[field]; return e; });
  }, []);

  const openModal = () => {
    const fullName = `${form.firstName} ${form.lastName}`.trim();
    setWaiver(prev => ({ ...prev, volunteerName: prev.volunteerName || fullName }));
    setShowModal(true);
  };

  const handleAgree = () => {
    setTermsAccepted(true);
    setShowModal(false);
    setErrors(prev => { const e = { ...prev }; delete e.terms; return e; });
  };

  const validate = () => {
    const e = {};
    if (!form.firstName.trim()) e.firstName = 'First name is required';
    else if (form.firstName.trim().length > 50) e.firstName = 'Max 50 characters';
    if (!form.lastName.trim()) e.lastName = 'Last name is required';
    else if (form.lastName.trim().length > 50) e.lastName = 'Max 50 characters';
    if (!form.email.trim()) e.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = 'Invalid email format';
    if (form.phone && !isValidUsPhone(form.phone)) e.phone = PHONE_ERROR;
    const dobDate = form.dob ? new Date(form.dob) : null;
    if (!form.dob) e.dob = 'Date of birth is required';
    const ageYears = dobDate ? Math.floor((Date.now() - dobDate.getTime()) / (365.25 * 24 * 60 * 60 * 1000)) : 99;
    if (ageYears < 18 && !form.parentName.trim()) e.parentName = 'Parent/Guardian name is required for volunteers under 18';
    if (!form.emergencyName.trim()) e.emergencyName = 'Emergency contact name is required';
    if (!form.emergencyCell.trim()) e.emergencyCell = 'Emergency phone number is required';
    else if (!isValidUsPhone(form.emergencyCell)) e.emergencyCell = PHONE_ERROR;
    if (!form.signature.trim()) e.signature = 'Signature is required';
    else if (!isValidSignature(form.signature)) e.signature = SIGNATURE_ERROR;
    if (!termsAccepted) e.terms = 'You must read and agree to the terms and conditions';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    if (abortRef.current) abortRef.current.abort();
    abortRef.current = new AbortController();

    setSubmitting(true);
    try {
      const dobDate = form.dob ? new Date(form.dob) : null;
      const ageYears = dobDate ? Math.floor((Date.now() - dobDate.getTime()) / (365.25 * 24 * 60 * 60 * 1000)) : 99;
      const ageCategory = ageYears < 18 ? 'Child' : 'Adult';
      const payload = {
        FirstName: form.firstName,
        LastName: form.lastName,
        Email: form.email,
        Phone: form.phone.replace(/\D/g, '') || 'NA',
        Age: ageCategory,
        Seva: 'Summer Camp Volunteer',
        CreatedDate: todayISO(),
        ParentName: ageCategory === 'Child' ? form.parentName : 'NA',
        Comments: form.comments || 'NA',
        Center_Id: 2,
        Emergency_Name: form.emergencyName || 'NA',
        Emergency_Con: form.emergencyCell.replace(/\D/g, '') || 'NA',
        Singnature: form.signature || 'NA',
        SingnatureDate: new Date().toISOString(),
      };

      await volunteerService.insertVolunteer(payload, abortRef.current.signal);

      const s = (v) => (v !== null && v !== undefined && String(v).trim() !== '') ? String(v) : 'NA';
      const fmtPhone = (digits) => {
        const d = (digits || '').replace(/\D/g, '');
        if (d.length === 10) return `(${d.slice(0,3)}) ${d.slice(3,6)}-${d.slice(6)}`;
        return d || 'NA';
      };
      const dobFormatted = form.dob
        ? new Date(form.dob + 'T00:00:00').toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })
        : 'NA';

      const emailPayload = {
        emailFrom: 'NA',
        emailTo: s(form.email),
        emailSubject: 'NA',
        emailBody: 'NA',
        attachment: 'N',
        userPhoneNumber: fmtPhone(form.phone),
        location: 'VolunteerRegistration',
        appContent: {
          userName: s(form.signature),
          programName: s(`${form.firstName} ${form.lastName}`.trim()),
          appSchedule: 'Summer Camp Volunteer',
          attendee: 'Atlanta',
          date: s(today()),
          orderType: fmtPhone(form.emergencyCell),
          orderNo: 'NA',
          paymentMode: s(form.parentName),
          amount: '0',
          email: fmtPhone(form.phone),
          phone: fmtPhone(form.phone),
          pickupPerson: s(form.signature),
          pickupPersonContact: 'NA',
          instructions: dobFormatted,
          itemData: s(today()),
          pickUpDate: 'NA',
          pickUpTime: s(form.email),
          paymentType: 'NA',
          billingAddress: 'NA',
          address: 'NA',
          pickupOrReceiver: 'NA',
          orderReady: s(form.email),
          orderReadyLastLine: 'NA',
          headingDate: s(form.emergencyName),
          headingTime: 'NA',
          startDate: dobFormatted,
          endDate: s(new Date().toISOString()),
          transactionId: 'NA',
        },
      };

      volunteerService.sendConfirmationEmail(emailPayload, abortRef.current.signal)
        .then(() => setEmailSent(true))
        .catch(() => setEmailSent(false));

      setSubmitted(true);
    } catch (err) {
      if (err?.name === 'CanceledError' || err?.name === 'AbortError') return;
      setErrors(prev => ({ ...prev, submit: 'Submission failed. Please try again.' }));
    } finally {
      setSubmitting(false);
    }
  };

  const org = getOrganizationConfig();

  if (submitted) {
    return (
      <div className="volreg-page">
        <div className="volreg-container">
          <div className="volreg-card">
            <div className="volreg-success">
              <div className="volreg-success-icon">✓</div>
              <h2 className="volreg-success-title">Registration Complete!</h2>
              {emailSent ? (
                <p className="volreg-success-sub">
                  Thank you for volunteering at Summer Camp! A confirmation email has been sent to <strong>{form.email}</strong>.
                </p>
              ) : (
                <p className="volreg-success-sub">
                  Thank you for volunteering at Summer Camp! We have received your registration.
                  <span className="volreg-success-note"> Note: We could not send a confirmation email at this time — please keep a note of your registration.</span>
                </p>
              )}
              <button className="volreg-success-back-btn" onClick={() => navigate('/')}>
                ← Back to Home
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="volreg-page">

      {/* ── Dark Hero Banner ── */}
      <div className="volreg-hero">
        <div className="volreg-hero-inner">
          <div className="volreg-hero-text">
            <p className="volreg-hero-desc">
              The success of YugalKunj (YK) depends upon enthusiastic, selfless volunteers who are keen to give back their talent and time to the community in which they live. Whether you foresee yourself as an occasional volunteer committing to a few hours every month or as a committed member who can volunteer for several hours in a week, <strong style={{color:'#b45309',fontWeight:700}}>we need you!</strong>
            </p>
          </div>
          <div className="volreg-hero-award">
            🏅 YugalKunj is approved as an official Certifying Organization for the <strong>President's Volunteer Service Award</strong> and we have been able to recognize the great work and commitment of our youth as well as adult volunteers. This not only delivers a powerful message during our college application process, provides the necessary motivation to our continuing volunteers but also encourages others to take action.
          </div>
        </div>
      </div>

      <div className="volreg-container">
        <div className="volreg-card">
          <form onSubmit={handleSubmit} noValidate>

            {/* ── Personal Information ── */}
            <p className="volreg-section-title">
              <span>👤</span> Personal Information
            </p>

            <div className="volreg-grid-2">
              <div className="volreg-field">
                <label className="volreg-label">First Name <span className="req">*</span></label>
                <input
                  className={`volreg-input${errors.firstName ? ' error' : ''}`}
                  type="text" maxLength={50} placeholder="First name"
                  value={form.firstName}
                  onChange={e => handleField('firstName', e.target.value)}
                />
                {errors.firstName && <span className="volreg-error-msg">{errors.firstName}</span>}
              </div>

              <div className="volreg-field">
                <label className="volreg-label">Last Name <span className="req">*</span></label>
                <input
                  className={`volreg-input${errors.lastName ? ' error' : ''}`}
                  type="text" maxLength={50} placeholder="Last name"
                  value={form.lastName}
                  onChange={e => handleField('lastName', e.target.value)}
                />
                {errors.lastName && <span className="volreg-error-msg">{errors.lastName}</span>}
              </div>

              <div className="volreg-field">
                <label className="volreg-label">Email <span className="req">*</span></label>
                <input
                  className={`volreg-input${errors.email ? ' error' : ''}`}
                  type="email" maxLength={100} placeholder="your@email.com"
                  value={form.email}
                  onChange={e => handleField('email', e.target.value)}
                />
                {errors.email && <span className="volreg-error-msg">{errors.email}</span>}
              </div>

              <div className="volreg-field">
                <label className="volreg-label">Phone# <span className="text-gray-400 text-xs font-normal">(optional)</span></label>
                <input
                  className={`volreg-input${errors.phone ? ' error' : ''}`}
                  type="tel" maxLength={14} placeholder="(XXX) XXX-XXXX"
                  value={form.phone}
                  onChange={e => handlePhoneField('phone', e.target.value)}
                  inputMode="numeric"
                />
                {errors.phone && <span className="volreg-error-msg">{errors.phone}</span>}
              </div>
            </div>

            <hr className="volreg-divider" />

            {/* ── Date of Birth ── */}
            <p className="volreg-section-title">
              <span>🎂</span> Date of Birth
            </p>
            <div className="volreg-grid-2">
              <div className="volreg-field">
                <label className="volreg-label">
                  Date of Birth <span className="req">*</span>
                  <span className="text-amber-600 text-xs font-semibold ml-2">📎 Please provide Age Proof</span>
                </label>
                <input
                  className={`volreg-input${errors.dob ? ' error' : ''}`}
                  type="date"
                  max={todayISO()}
                  value={form.dob}
                  onChange={e => handleField('dob', e.target.value)}
                />
                {errors.dob && <span className="volreg-error-msg">{errors.dob}</span>}
              </div>
            </div>

            {form.dob && (() => {
              const d = new Date(form.dob);
              const yrs = Math.floor((Date.now() - d.getTime()) / (365.25 * 24 * 60 * 60 * 1000));
              return yrs < 18 ? (
                <div className="volreg-field mt-2">
                  <label className="volreg-label">Parent / Guardian Name <span className="req">*</span></label>
                  <input
                    className={`volreg-input${errors.parentName ? ' error' : ''}`}
                    type="text" maxLength={50} placeholder="Full name"
                    value={form.parentName}
                    onChange={e => handleField('parentName', e.target.value)}
                  />
                  {errors.parentName && <span className="volreg-error-msg">{errors.parentName}</span>}
                </div>
              ) : null;
            })()}

            <hr className="volreg-divider" />

            {/* ── Comments ── */}
            <p className="volreg-section-title">
              <span>💬</span> Comments
            </p>
            <textarea
              className="volreg-textarea" rows={2} placeholder="Any additional notes or availability..."
              value={form.comments}
              onChange={e => handleField('comments', e.target.value)}
            />

            <hr className="volreg-divider" />

            {/* ── Emergency Contact ── */}
            <p className="volreg-section-title">
              <span>🚨</span> Emergency Contact
            </p>
            <div className="volreg-grid-2">
              <div className="volreg-field">
                <label className="volreg-label">Contact Name <span className="req">*</span></label>
                <input
                  className={`volreg-input${errors.emergencyName ? ' error' : ''}`}
                  type="text" maxLength={100} placeholder="Full name"
                  value={form.emergencyName}
                  onChange={e => handleField('emergencyName', e.target.value)}
                />
                {errors.emergencyName && <span className="volreg-error-msg">{errors.emergencyName}</span>}
              </div>
              <div className="volreg-field">
                <label className="volreg-label">Phone# <span className="req">*</span></label>
                <input
                  className={`volreg-input${errors.emergencyCell ? ' error' : ''}`}
                  type="tel" maxLength={14} placeholder="(XXX) XXX-XXXX"
                  value={form.emergencyCell}
                  onChange={e => handlePhoneField('emergencyCell', e.target.value)}
                  inputMode="numeric"
                />
                {errors.emergencyCell && <span className="volreg-error-msg">{errors.emergencyCell}</span>}
              </div>
            </div>

            <hr className="volreg-divider" />

            {/* ── Signature ── */}
            <p className="volreg-section-title">
              <span>✍️</span> Signature
            </p>
            <div className="volreg-grid-2">
              <div className="volreg-field">
                <label className="volreg-label">Sign your name (typed) <span className="req">*</span></label>
                <input
                  className={`volreg-input${errors.signature ? ' error' : ''}`}
                  type="text" maxLength={100} placeholder="Type full name as signature"
                  value={form.signature}
                  onChange={e => handleField('signature', e.target.value)}
                />
                {errors.signature && <span className="volreg-error-msg">{errors.signature}</span>}
              </div>
              <div className="volreg-field">
                <label className="volreg-label">Date</label>
                <input className="volreg-input" type="text" readOnly value={today()} />
              </div>
            </div>

            <hr className="volreg-divider" />

            {/* ── Terms ── */}
            <div
              className={`volreg-terms-row${errors.terms ? ' border-red-400' : ''}`}
              style={{cursor: 'pointer'}}
              onClick={() => setTermsAccepted(prev => !prev)}
            >
              <div
                className={`volreg-custom-cb${termsAccepted ? ' checked' : ''}`}
                role="checkbox"
                aria-checked={termsAccepted}
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === ' ') { e.preventDefault(); setTermsAccepted(prev => !prev); } }}
              >
                {termsAccepted && (
                  <svg viewBox="0 0 12 10" fill="none" xmlns="http://www.w3.org/2000/svg" width="10" height="10">
                    <path d="M1 5l3.5 3.5L11 1" stroke="white" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                )}
              </div>
              <span className="volreg-terms-text">
                I (and parent or guardian if Volunteer is under 18) have carefully read the release and voluntarily agree to the{' '}
                <span
                  className="volreg-terms-link"
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); openModal(); }}
                >
                  Terms &amp; Conditions / Liability Waiver
                </span>
              </span>
            </div>
            {errors.terms && <span className="volreg-error-msg mt-1 block">{errors.terms}</span>}

            {errors.submit && (
              <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-600">
                {errors.submit}
              </div>
            )}

            <div className="mt-6">
              <button type="submit" className="volreg-submit-btn" disabled={submitting}>
                {submitting ? 'Submitting…' : 'Submit Summer Camp Volunteer Registration'}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* ── Waiver Modal ── */}
      {showModal && (
        <div className="volreg-modal-backdrop" onClick={() => setShowModal(false)}>
          <div className="volreg-modal" onClick={e => e.stopPropagation()}>
            <div className="volreg-modal-header">
              <span className="volreg-modal-title">🤝 Volunteer Liability Waiver</span>
              <button className="volreg-modal-close" onClick={() => setShowModal(false)}>×</button>
            </div>

            <div className="volreg-modal-body">
              <div className="volreg-modal-org">
                <h2>{org.fullName}</h2>
                <p>{org.address}</p>
                <p className="font-bold text-indigo-700 mt-1">Volunteer Terms, Conditions &amp; Liability Waiver</p>
              </div>

              <div className="volreg-waiver-section">
                <p className="text-sm text-gray-600 italic">
                  Yugal Kunj is grateful to you for offering your time and talent in furtherance of Yugal Kunj vision.
                  Every volunteer is requested to follow the rules and regulations.
                </p>
              </div>

              <div className="volreg-waiver-section">
                <p className="volreg-waiver-section-title">I understand and agree to the following:</p>
                {[
                  ['1.', `I am donating my time services and shall at no time be considered an employee or contractor of the ${org.fullName}.`],
                  ['2.', `Volunteers are not official representatives of ${org.shortName} and cannot act on behalf, unless given specific written instructions by the Board of Directors.`],
                  ['3.', 'Volunteers are encouraged to help each other and work together to create environment of selflessness, humility and harmony on the premises of Yugal Kunj.'],
                  ['4.', 'Volunteers should not engage themselves in any activity/solicitation/campaign of any nature for personal gain on the premises of Yugal Kunj.'],
                  ['5.', 'Volunteering is an honorable act, and volunteers are kindly requested to refrain from escalating any disagreements.'],
                  ['6.', `${org.shortName} may revoke volunteer privileges of individuals at any time, without providing any notice or reason.`],
                ].map(([num, text]) => (
                  <div key={num} className="flex gap-2 mb-2">
                    <span className="text-sm font-bold text-indigo-600 flex-shrink-0">{num}</span>
                    <p className="text-sm text-gray-600 leading-relaxed">{text}</p>
                  </div>
                ))}
              </div>

              <div className="volreg-waiver-section">
                <p className="volreg-waiver-section-title">Health and Medical Waiver</p>
                <p className="text-sm text-gray-600 leading-relaxed">
                  {`I affirm that I am physically fit and able to participate in the volunteer activities. I assume all risk of personal injury, sickness and expenses incurred as a result of any accident while volunteering for ${org.fullName}. I release ${org.shortName} from any liability related to any personal injury suffered by me while volunteering.`}
                </p>
              </div>

              <div className="volreg-waiver-section">
                <p className="volreg-waiver-section-title">Photographic Release</p>
                <p className="text-sm text-gray-600 leading-relaxed">
                  {`${org.shortName} may take videos, photographs or testimonials of volunteers and use the same on their websites or communications. I grant all rights, title, and interests in any photographs, images, or video in connection with my volunteer services.`}
                </p>
              </div>
            </div>

            <div className="volreg-modal-footer">
              <button className="volreg-modal-close-btn" onClick={() => setShowModal(false)}>Close</button>
              <button className="volreg-modal-agree-btn" onClick={handleAgree}>I Agree</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
